import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  addDoc,
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Check } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { useCart } from '../context/CartContext';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import { useToast } from '../hooks/useToast';
import { getProductImageObjectPosition } from '../data/products';
import { db, functions } from '../firebase';

const initialFormData = {
  fullName: '',
  email: '',
  phone: '',
  address: '',
  city: '',
  zip: '',
  country: '',
  useDifferentBilling: false,
  billingAddress: '',
  billingCity: '',
  billingZip: '',
  billingCountry: '',
  orderNotes: '',
  shippingMethod: 'packeta_parcel_locker',
  paymentMethod: 'cod',
  acceptTerms: false,
  acceptPrivacy: false,
  saveAbandonedCheckout: false,
};

const ABANDONED_CHECKOUT_STORAGE_KEY = 'lumenverde-abandoned-checkout';
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const GLS_WIDGET_SCRIPT_URL = 'https://map.gls-hungary.com/widget/gls-dpm.js';
const PACKETA_WIDGET_SCRIPT_URL = 'https://widget.packeta.com/v6/www/js/library.js';
const DEFAULT_PACKETA_WIDGET_API_KEY = 'e0fbb20a0e4a34b1';
const SHIPPING_COSTS = {
  gls_home: 2190,
  gls_parcel_locker: 1590,
  packeta_parcel_locker: 1190,
  personal_pickup_omega: 0,
};
const ALLOWED_COUNTRY_CODES = new Set(['HU', 'AT', 'SK', 'RO', 'HR']);
const COUNTRY_CODE_ALIASES = {
  MAGYARORSZAG: 'HU',
  'MAGYARORSZÁG': 'HU',
  HUNGARY: 'HU',
  AUSZTRIA: 'AT',
  AUSTRIA: 'AT',
  SZLOVAKIA: 'SK',
  SZLOVAKIAI: 'SK',
  SLOVAKIA: 'SK',
  ROMANIA: 'RO',
  'ROMÁNIA': 'RO',
  HORVATORSZAG: 'HR',
  'HORVÁTORSZÁG': 'HR',
  CROATIA: 'HR',
};
const GLS_SELECTION_EVENTS = [
  'change',
  'gls-dpm-selected',
  'parcelshopselected',
  'parcelshopchange',
  'pickup-point-selected',
  'locker-selected',
  'selectionchange',
];

const pickFirstString = (...values) => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
      return String(value);
    }
  }
  return '';
};

const normalizeCountryCode = (value) => {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  if (!trimmed) return '';
  const upper = trimmed.toUpperCase();
  if (ALLOWED_COUNTRY_CODES.has(upper)) return upper;
  return COUNTRY_CODE_ALIASES[upper] || '';
};

const toObject = (value) => {
  if (!value) return null;
  if (typeof value === 'object') return value;
  if (typeof value !== 'string') return null;

  const trimmed = value.trim();
  if (!trimmed) return null;

  try {
    const parsed = JSON.parse(trimmed);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
};

const collectDetailCandidates = (rawDetail) => {
  const root = toObject(rawDetail);
  if (!root) return [];

  const keys = [
    'detail',
    'data',
    'value',
    'selected',
    'selection',
    'point',
    'pickupPoint',
    'parcelLocker',
    'parcelShop',
    'parcelshop',
  ];
  const queue = [root];
  const seen = new Set();
  const candidates = [];

  while (queue.length > 0) {
    const current = queue.shift();
    if (!current || typeof current !== 'object') continue;
    if (seen.has(current)) continue;
    seen.add(current);
    candidates.push(current);

    keys.forEach((key) => {
      const nested = toObject(current[key]);
      if (nested) queue.push(nested);
    });
  }

  return candidates;
};

const normalizeParcelLocker = (rawDetail) => {
  const candidates = collectDetailCandidates(rawDetail);

  for (const detail of candidates) {
    const contact = detail.contact && typeof detail.contact === 'object' ? detail.contact : {};
    const id = pickFirstString(
      detail.id,
      detail.externalId,
      detail.external_id,
      detail.parcelShopId,
      detail.parcelshopId,
      detail.parcelLockerId,
      detail.parcel_locker_id,
      detail.pickupPointId,
      detail.pickup_point_id,
      detail.code,
      detail.shopCode,
      detail.number,
      contact.id,
      contact.externalId,
      contact.external_id,
      contact.code,
      contact.number,
    );
    if (!id) continue;

    return {
      id,
      name: pickFirstString(
        detail.name,
        detail.shopName,
        detail.parcelShopName,
        detail.parcelLockerName,
        detail.locationName,
        contact.name,
        id,
      ),
      type: pickFirstString(detail.type, detail.deliveryPointType, detail.pointType),
      address: pickFirstString(
        detail.address,
        detail.street,
        detail.streetAddress,
        detail.streetName,
        contact.address,
        contact.street,
      ),
      city: pickFirstString(detail.city, detail.town, contact.city),
      zip: pickFirstString(
        detail.zip,
        detail.postCode,
        detail.postalCode,
        detail.postcode,
        contact.zip,
        contact.postCode,
        contact.postalCode,
        contact.postcode,
      ),
    };
  }

  return null;
};

const normalizeParcelLockerFromDialog = (dialog) => {
  if (!dialog) return null;

  const candidates = [
    dialog.value,
    dialog.selected,
    dialog.selection,
    dialog.selectedPoint,
    dialog.selectedParcelLocker,
    dialog.pickupPoint,
    dialog.parcelShop,
    dialog.parcelLocker,
    typeof dialog.getAttribute === 'function' ? dialog.getAttribute('value') : null,
    typeof dialog.getAttribute === 'function' ? dialog.getAttribute('data-selected') : null,
  ];

  for (const candidate of candidates) {
    const normalized = normalizeParcelLocker(candidate);
    if (normalized) return normalized;
  }

  return null;
};

const normalizePacketaPickupPoint = (rawPoint) => {
  if (!rawPoint || typeof rawPoint !== 'object') return null;

  const id = pickFirstString(
    rawPoint.id,
    rawPoint.pickupPointId,
    rawPoint.placeId,
    rawPoint.carrierPickupPointId,
    rawPoint.carrier_point_id,
    rawPoint.code,
  );
  if (!id) return null;

  const streetWithHouseNumber = [pickFirstString(rawPoint.street), pickFirstString(rawPoint.houseNumber)]
    .filter(Boolean)
    .join(' ')
    .trim();

  return {
    id,
    name: pickFirstString(rawPoint.name, rawPoint.place, rawPoint.formattedValue, id),
    type: 'packeta',
    address: pickFirstString(streetWithHouseNumber, rawPoint.address),
    city: pickFirstString(rawPoint.city),
    zip: pickFirstString(rawPoint.zip, rawPoint.postCode, rawPoint.postalCode),
  };
};

const sanitizePickupPoint = (rawPoint) => {
  if (!rawPoint) return null;

  const normalized = normalizePacketaPickupPoint(rawPoint) || normalizeParcelLocker(rawPoint);
  if (normalized?.id) return normalized;

  const source = typeof rawPoint === 'object' ? rawPoint : null;
  if (!source) return null;

  const id = pickFirstString(source.id);
  if (!id) return null;

  return {
    id,
    name: pickFirstString(source.name, id),
    type: pickFirstString(source.type),
    address: pickFirstString(source.address),
    city: pickFirstString(source.city),
    zip: pickFirstString(source.zip),
  };
};

export default function Checkout() {
  const navigate = useNavigate();
  const { items, getCartTotal, clearCart } = useCart();
  const { user } = useCustomerAuth();
  const { toast } = useToast();
  const glsDialogRef = useRef(null);
  const draftCredentialsRef = useRef(null);
  const draftRequestQueueRef = useRef(Promise.resolve());
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGlsWidgetReady, setIsGlsWidgetReady] = useState(false);
  const [isPacketaWidgetReady, setIsPacketaWidgetReady] = useState(false);
  const [selectedParcelLocker, setSelectedParcelLocker] = useState(null);
  const [selectedPacketaPickupPoint, setSelectedPacketaPickupPoint] = useState(null);
  const [draftSaveStatus, setDraftSaveStatus] = useState('idle');
  const packetaApiKey = (
    process.env.REACT_APP_PACKETA_WIDGET_API_KEY || DEFAULT_PACKETA_WIDGET_API_KEY
  ).trim();
  const isGlsParcelLocker = formData.shippingMethod === 'gls_parcel_locker';
  const isPacketaParcelLocker = formData.shippingMethod === 'packeta_parcel_locker';
  const selectedPickupPoint = isGlsParcelLocker
    ? selectedParcelLocker
    : isPacketaParcelLocker
      ? selectedPacketaPickupPoint
      : null;

  const subtotal = getCartTotal();
  const shippingCost = SHIPPING_COSTS[formData.shippingMethod] ?? SHIPPING_COSTS.packeta_parcel_locker;
  const total = subtotal + shippingCost;

  const readDraftCredentials = useCallback(() => {
    if (draftCredentialsRef.current) return draftCredentialsRef.current;
    if (typeof window === 'undefined') return null;
    try {
      const stored = JSON.parse(localStorage.getItem(ABANDONED_CHECKOUT_STORAGE_KEY) || 'null');
      if (stored?.draftId && stored?.token) {
        draftCredentialsRef.current = stored;
        return stored;
      }
    } catch {
      localStorage.removeItem(ABANDONED_CHECKOUT_STORAGE_KEY);
    }
    return null;
  }, []);

  const getOrCreateDraftCredentials = useCallback(() => {
    const existing = readDraftCredentials();
    if (existing) return existing;
    const credentials = {
      draftId: crypto.randomUUID(),
      token: `${crypto.randomUUID()}${crypto.randomUUID().replaceAll('-', '')}`,
    };
    draftCredentialsRef.current = credentials;
    localStorage.setItem(ABANDONED_CHECKOUT_STORAGE_KEY, JSON.stringify(credentials));
    return credentials;
  }, [readDraftCredentials]);

  const queueDraftRequest = useCallback((requestData) => {
    const run = async () => {
      const saveAbandonedCheckout = httpsCallable(functions, 'saveAbandonedCheckout');
      return saveAbandonedCheckout(requestData);
    };
    draftRequestQueueRef.current = draftRequestQueueRef.current.catch(() => undefined).then(run);
    return draftRequestQueueRef.current;
  }, []);

  const deleteSavedDraft = useCallback(async () => {
    const credentials = readDraftCredentials();
    if (!credentials) return;
    try {
      await queueDraftRequest({ action: 'delete', ...credentials });
    } finally {
      draftCredentialsRef.current = null;
      localStorage.removeItem(ABANDONED_CHECKOUT_STORAGE_KEY);
    }
  }, [queueDraftRequest, readDraftCredentials]);

  useEffect(() => {
    if (
      !formData.saveAbandonedCheckout ||
      !EMAIL_PATTERN.test(formData.email.trim()) ||
      items.length === 0 ||
      isSubmitting
    ) {
      return undefined;
    }

    setDraftSaveStatus('saving');
    const saveTimer = window.setTimeout(async () => {
      const pickupPoint = sanitizePickupPoint(selectedPickupPoint);
      const payload = {
        customer: {
          name: formData.fullName,
          email: formData.email,
          phone: formData.phone,
          address: formData.address,
          city: formData.city,
          zip: formData.zip,
          country: normalizeCountryCode(formData.country),
        },
        billing: formData.useDifferentBilling
          ? {
              address: formData.billingAddress,
              city: formData.billingCity,
              zip: formData.billingZip,
              country: normalizeCountryCode(formData.billingCountry),
            }
          : null,
        items: items.map((item) => ({
          name: item.product.name,
          quantity: item.quantity,
          price: item.product.price,
        })),
        shipping: formData.shippingMethod,
        payment: formData.paymentMethod,
        pickupPoint,
        subtotal,
        shippingCost,
        total,
      };

      try {
        const credentials = getOrCreateDraftCredentials();
        await queueDraftRequest({
          action: 'upsert',
          consent: true,
          payload,
          ...credentials,
        });
        setDraftSaveStatus('saved');
      } catch {
        setDraftSaveStatus('error');
      }
    }, 1200);

    return () => window.clearTimeout(saveTimer);
  }, [
    formData,
    getOrCreateDraftCredentials,
    isSubmitting,
    items,
    queueDraftRequest,
    selectedPickupPoint,
    shippingCost,
    subtotal,
    total,
  ]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (window.customElements?.get('gls-dpm-dialog')) {
      setIsGlsWidgetReady(true);
      return undefined;
    }

    const existingScript = document.querySelector('script[data-gls-dpm-widget="true"]');
    const script = existingScript || document.createElement('script');
    const handleLoad = () => {
      setIsGlsWidgetReady(Boolean(window.customElements?.get('gls-dpm-dialog')));
    };
    const handleError = () => {
      setIsGlsWidgetReady(false);
    };

    script.addEventListener('load', handleLoad);
    script.addEventListener('error', handleError);

    if (!existingScript) {
      script.type = 'module';
      script.src = GLS_WIDGET_SCRIPT_URL;
      script.setAttribute('data-gls-dpm-widget', 'true');
      document.head.appendChild(script);
    }

    return () => {
      script.removeEventListener('load', handleLoad);
      script.removeEventListener('error', handleError);
    };
  }, []);

  useEffect(() => {
    const uid = user?.uid;
    if (!uid) return undefined;

    let isCancelled = false;

    const loadSavedProfile = async () => {
      try {
        const profileSnap = await getDoc(doc(db, 'customerProfiles', uid));
        if (!profileSnap.exists() || isCancelled) return;
        const profile = profileSnap.data() || {};
        const contact = profile.contact || {};
        const shipping = profile.shipping || {};
        const billing = profile.billing || {};
        const shippingCountry = normalizeCountryCode(shipping.country);
        const billingCountry = normalizeCountryCode(billing.country);
        const pickupPoint = sanitizePickupPoint(shipping.pickupPoint);

        setFormData((prev) => ({
          ...prev,
          fullName: contact.fullName || prev.fullName,
          email: contact.email || prev.email || user?.email || '',
          phone: contact.phone || prev.phone,
          address: shipping.address || prev.address,
          city: shipping.city || prev.city,
          zip: shipping.zip || prev.zip,
          country: shippingCountry || prev.country,
          shippingMethod:
            shipping.shippingMethod && SHIPPING_COSTS[shipping.shippingMethod] !== undefined
              ? shipping.shippingMethod
              : prev.shippingMethod,
          useDifferentBilling:
            typeof billing.useDifferentBilling === 'boolean'
              ? billing.useDifferentBilling
              : prev.useDifferentBilling,
          billingAddress: billing.address || prev.billingAddress,
          billingCity: billing.city || prev.billingCity,
          billingZip: billing.zip || prev.billingZip,
          billingCountry: billingCountry || prev.billingCountry,
        }));

        if (pickupPoint?.id) {
          const normalizedType = String(pickupPoint.type || '').toLowerCase();
          const savedShippingMethod = String(shipping.shippingMethod || '');
          const isPacketaPickup =
            savedShippingMethod === 'packeta_parcel_locker' || normalizedType.includes('packeta');
          if (isPacketaPickup) {
            setSelectedPacketaPickupPoint(pickupPoint);
            setSelectedParcelLocker(null);
          } else {
            setSelectedParcelLocker(pickupPoint);
            setSelectedPacketaPickupPoint(null);
          }
        }
      } catch {
        // Do not block checkout if profile read fails.
      }
    };

    loadSavedProfile();
    return () => {
      isCancelled = true;
    };
  }, [user]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (typeof window.Packeta?.Widget?.pick === 'function') {
      setIsPacketaWidgetReady(true);
      return undefined;
    }

    const existingScript = document.querySelector('script[data-packeta-widget="true"]');
    const script = existingScript || document.createElement('script');
    const handleLoad = () => {
      setIsPacketaWidgetReady(typeof window.Packeta?.Widget?.pick === 'function');
    };
    const handleError = () => {
      setIsPacketaWidgetReady(false);
    };

    script.addEventListener('load', handleLoad);
    script.addEventListener('error', handleError);

    if (!existingScript) {
      script.src = PACKETA_WIDGET_SCRIPT_URL;
      script.setAttribute('data-packeta-widget', 'true');
      document.head.appendChild(script);
    }

    return () => {
      script.removeEventListener('load', handleLoad);
      script.removeEventListener('error', handleError);
    };
  }, []);

  useEffect(() => {
    if (!isGlsParcelLocker) return undefined;
    const dialog = glsDialogRef.current;
    if (!dialog) return undefined;

    const applySelection = (payload) => {
      const normalized = normalizeParcelLocker(payload) || normalizeParcelLockerFromDialog(dialog);
      if (!normalized) return;
      setSelectedParcelLocker(normalized);
      setErrors((prev) => ({ ...prev, parcelLocker: undefined }));
    };

    const handleSelectionEvent = (event) => {
      applySelection(
        event?.detail ??
          event?.target?.value ??
          event?.target?.selected ??
          event?.target?.selectedPoint ??
          event?.target?.selectedParcelLocker,
      );
    };

    const handleDialogClose = () => {
      applySelection(
        dialog.value ??
          dialog.selected ??
          dialog.selection ??
          dialog.selectedPoint ??
          dialog.selectedParcelLocker ??
          dialog.pickupPoint,
      );
    };

    GLS_SELECTION_EVENTS.forEach((eventName) => dialog.addEventListener(eventName, handleSelectionEvent));
    dialog.addEventListener('close', handleDialogClose);
    dialog.addEventListener('confirm', handleDialogClose);

    return () => {
      GLS_SELECTION_EVENTS.forEach((eventName) =>
        dialog.removeEventListener(eventName, handleSelectionEvent),
      );
      dialog.removeEventListener('close', handleDialogClose);
      dialog.removeEventListener('confirm', handleDialogClose);
    };
  }, [isGlsParcelLocker, isGlsWidgetReady]);

  const openGlsParcelLockerPicker = () => {
    const dialog = glsDialogRef.current;
    if (!dialog) return;
    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
      return;
    }
    if (typeof dialog.open === 'function') {
      dialog.open();
      return;
    }
    dialog.setAttribute('open', 'true');
  };

  const openPacketaPickupPointPicker = () => {
    if (!packetaApiKey) {
      setErrors((prev) => ({
        ...prev,
        packetaPickupPoint: 'A Packeta kiválasztó nincs beállítva (hiányzó API kulcs).',
      }));
      return;
    }

    const packetaPickFn = window.Packeta?.Widget?.pick;
    if (typeof packetaPickFn !== 'function') {
      setErrors((prev) => ({
        ...prev,
        packetaPickupPoint: 'A Packeta kiválasztó még nem töltött be.',
      }));
      return;
    }

    try {
      packetaPickFn(
        packetaApiKey,
        (point) => {
          const normalized = normalizePacketaPickupPoint(point);
          if (!normalized) return;
          setSelectedPacketaPickupPoint(normalized);
          setErrors((prev) => ({ ...prev, packetaPickupPoint: undefined }));
        },
        {
          language: 'hu',
          country: 'hu',
        },
      );
    } catch {
      setErrors((prev) => ({
        ...prev,
        packetaPickupPoint: 'Nem sikerült megnyitni a Packeta kiválasztót.',
      }));
    }
  };

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    const checked = e.target.checked;

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    if (name === 'saveAbandonedCheckout') {
      if (checked) {
        setDraftSaveStatus(EMAIL_PATTERN.test(formData.email.trim()) ? 'saving' : 'waitingForEmail');
      } else {
        setDraftSaveStatus('idle');
        deleteSavedDraft().catch(() => setDraftSaveStatus('error'));
      }
    }

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
    if (name === 'shippingMethod' && value !== 'gls_parcel_locker' && errors.parcelLocker) {
      setErrors((prev) => ({ ...prev, parcelLocker: undefined }));
    }
    if (name === 'shippingMethod' && value !== 'packeta_parcel_locker' && errors.packetaPickupPoint) {
      setErrors((prev) => ({ ...prev, packetaPickupPoint: undefined }));
    }
  };

  const validate = () => {
    const newErrors = {};
    const normalizedShippingCountry = normalizeCountryCode(formData.country);
    const normalizedBillingCountry = normalizeCountryCode(formData.billingCountry);

    if (!formData.fullName.trim()) newErrors.fullName = 'Teljes név megadása kötelező';
    if (!formData.email.trim()) {
      newErrors.email = 'Email cím megadása kötelező';
    } else if (!EMAIL_PATTERN.test(formData.email)) {
      newErrors.email = 'Kérjük, adj meg érvényes email címet';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Mobilszám megadása kötelező';
    }
    if (!formData.address.trim()) newErrors.address = 'Cím megadása kötelező';
    if (!formData.city.trim()) newErrors.city = 'Város megadása kötelező';
    if (!formData.zip.trim()) newErrors.zip = 'Irányítószám megadása kötelező';
    if (!normalizedShippingCountry) newErrors.country = 'Válassz érvényes országot a listából';

    if (formData.useDifferentBilling) {
      if (!formData.billingAddress.trim()) newErrors.billingAddress = 'Számlázási cím megadása kötelező';
      if (!formData.billingCity.trim()) newErrors.billingCity = 'Számlázási város megadása kötelező';
      if (!formData.billingZip.trim()) newErrors.billingZip = 'Számlázási irányítószám megadása kötelező';
      if (!normalizedBillingCountry) {
        newErrors.billingCountry = 'Válassz érvényes számlázási országot a listából';
      }
    }

    if (!formData.acceptTerms) newErrors.acceptTerms = 'El kell fogadnod a feltételeket';
    if (!formData.acceptPrivacy) {
      newErrors.acceptPrivacy = 'Tudomásul kell venned az adatvédelmi tájékoztatót';
    }
    if (isGlsParcelLocker && !selectedParcelLocker?.id) {
      newErrors.parcelLocker = 'GLS csomagautomata választása kötelező';
    }
    if (isPacketaParcelLocker && !selectedPacketaPickupPoint?.id) {
      newErrors.packetaPickupPoint = 'Packeta csomagpont választása kötelező';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      toast({
        title: 'Kérjük, ellenőrizd az adataidat',
        description: 'Néhány kötelező mező hiányzik vagy érvénytelen.',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);

    await new Promise((resolve) => setTimeout(resolve, 1500));

    const orderNumber = `LV-${Date.now().toString(36).toUpperCase()}`;
    const invoiceDownloadToken = crypto.randomUUID();
    const shippingCountry = normalizeCountryCode(formData.country);
    const billingCountry = normalizeCountryCode(formData.billingCountry);
    const pickupPoint = sanitizePickupPoint(
      isGlsParcelLocker
        ? selectedParcelLocker
        : isPacketaParcelLocker
          ? selectedPacketaPickupPoint
          : null,
    );

    const order = {
      orderNumber,
      invoiceDownloadToken,
      items: items.map((item) => ({
        name: item.product.name,
        quantity: item.quantity,
        price: item.product.price,
      })),
      ...(user?.uid ? { customerUid: user.uid } : {}),
      shipping: formData.shippingMethod,
      shippingCost,
      payment: formData.paymentMethod,
      subtotal,
      total,
      customer: {
        name: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        city: formData.city,
        zip: formData.zip,
        country: shippingCountry,
      },
      billing: formData.useDifferentBilling
        ? {
            address: formData.billingAddress,
            city: formData.billingCity,
            zip: formData.billingZip,
            country: billingCountry,
          }
        : null,
      pickupPoint,
      createdAt: new Date().toISOString(),
      createdAtServer: serverTimestamp(),
    };

    let orderDocumentId = '';
    try {
      const orderRef = await addDoc(collection(db, 'orders'), order);
      orderDocumentId = orderRef.id;
      const existingOrders = JSON.parse(localStorage.getItem('lumenverde-orders') || '[]');
      const localOrder = {
        ...order,
        createdAtServer: new Date().toISOString(),
      };
      localStorage.setItem('lumenverde-orders', JSON.stringify([...existingOrders, localOrder]));
      window.dispatchEvent(new Event('lumenverde-orders-updated'));
    } catch (error) {
      setIsSubmitting(false);
      const errorCode = typeof error?.code === 'string' ? ` (${error.code})` : '';
      toast({
        title: 'Nem sikerült menteni a rendelést',
        description: `Ellenőrizd a Firestore beállításokat és a jogosultságokat, majd próbáld újra${errorCode}.`,
        variant: 'destructive',
      });
      return;
    }

    if (user?.uid) {
      try {
        const profilePayload = {
          uid: user.uid,
          contact: {
            fullName: formData.fullName,
            email: formData.email,
            phone: formData.phone,
          },
          shipping: {
            address: formData.address,
            city: formData.city,
            zip: formData.zip,
            country: shippingCountry,
            shippingMethod: formData.shippingMethod,
            pickupPoint: pickupPoint || null,
          },
          billing: {
            useDifferentBilling: formData.useDifferentBilling,
            address: formData.useDifferentBilling ? formData.billingAddress : '',
            city: formData.useDifferentBilling ? formData.billingCity : '',
            zip: formData.useDifferentBilling ? formData.billingZip : '',
            country: formData.useDifferentBilling ? billingCountry : '',
          },
          updatedAt: serverTimestamp(),
        };
        await setDoc(doc(db, 'customerProfiles', user.uid), profilePayload, { merge: true });
      } catch (error) {
        const profileErrorCode = typeof error?.code === 'string' ? ` (${error.code})` : '';
        toast({
          title: 'A rendelés mentve lett',
          description: `A profil adatok mentése most nem sikerült${profileErrorCode}, de a rendelést rögzítettük.`,
          variant: 'destructive',
        });
      }
    }

    await deleteSavedDraft().catch(() => undefined);

    clearCart();
    const thankYouParams = new URLSearchParams({
      order: orderNumber,
      id: orderDocumentId,
      token: invoiceDownloadToken,
    });
    navigate(`/thank-you?${thankYouParams.toString()}`);
  };

  if (items.length === 0) {
    return (
      <Layout>
        <div className="section-padding">
          <div className="container-zinga text-center py-16">
            <h1 className="font-display text-2xl font-bold mb-4">A kosarad üres</h1>
            <Link to="/shop" className="btn-primary inline-flex items-center gap-2">
              Vásárlás megkezdése
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="section-padding overflow-x-hidden">
        <div className="container-zinga">
          <Link
            to="/cart"
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Vissza a kosárhoz
          </Link>

          <h1 className="font-display text-3xl md:text-4xl font-bold mb-8">Pénztár</h1>

          <form onSubmit={handleSubmit}>
            <div className="grid lg:grid-cols-3 gap-8 md:gap-12">
              <div className="lg:col-span-2 space-y-8">
                <section className="card-zinga">
                  <h2 className="font-display text-xl font-semibold mb-6">Kapcsolattartási adatok</h2>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium mb-2">Teljes név *</label>
                      <input
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleChange}
                        className={`input-zinga ${errors.fullName ? 'border-destructive' : ''}`}
                        placeholder="Kovács János"
                      />
                      {errors.fullName && <p className="text-sm text-destructive mt-1">{errors.fullName}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Email *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className={`input-zinga ${errors.email ? 'border-destructive' : ''}`}
                        placeholder="kovacs.janos@email.hu"
                      />
                      {errors.email && <p className="text-sm text-destructive mt-1">{errors.email}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Mobilszám *</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className={`input-zinga ${errors.phone ? 'border-destructive' : ''}`}
                        placeholder="+36 30 123 4567"
                        autoComplete="tel"
                        maxLength={40}
                        required
                        aria-invalid={Boolean(errors.phone)}
                      />
                      {errors.phone && (
                        <p className="text-sm text-destructive mt-1">{errors.phone}</p>
                      )}
                    </div>
                  </div>
                </section>

                <section className="card-zinga">
                  <h2 className="font-display text-xl font-semibold mb-6">Szállítási cím</h2>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium mb-2">Utca, házszám *</label>
                      <input
                        type="text"
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                        className={`input-zinga ${errors.address ? 'border-destructive' : ''}`}
                        placeholder="Kossuth utca 12."
                      />
                      {errors.address && <p className="text-sm text-destructive mt-1">{errors.address}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Város *</label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        className={`input-zinga ${errors.city ? 'border-destructive' : ''}`}
                        placeholder="Budapest"
                      />
                      {errors.city && <p className="text-sm text-destructive mt-1">{errors.city}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Irányítószám *</label>
                      <input
                        type="text"
                        name="zip"
                        value={formData.zip}
                        onChange={handleChange}
                        className={`input-zinga ${errors.zip ? 'border-destructive' : ''}`}
                        placeholder="1056"
                      />
                      {errors.zip && <p className="text-sm text-destructive mt-1">{errors.zip}</p>}
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium mb-2">Ország *</label>
                      <select
                        name="country"
                        value={formData.country}
                        onChange={handleChange}
                        className={`input-zinga ${errors.country ? 'border-destructive' : ''}`}
                      >
                        <option value="">Válassz országot</option>
                        <option value="HU">Magyarország</option>
                        <option value="AT">Ausztria</option>
                        <option value="SK">Szlovákia</option>
                        <option value="RO">Románia</option>
                        <option value="HR">Horvátország</option>
                      </select>
                      {errors.country && <p className="text-sm text-destructive mt-1">{errors.country}</p>}
                    </div>
                  </div>

                  <label className="flex items-center gap-3 mt-6 cursor-pointer">
                    <input
                      type="checkbox"
                      name="useDifferentBilling"
                      checked={formData.useDifferentBilling}
                      onChange={handleChange}
                      className="sr-only"
                    />
                    <div
                      className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                        formData.useDifferentBilling ? 'bg-primary border-primary' : 'border-border'
                      }`}
                    >
                      {formData.useDifferentBilling && <Check className="w-3 h-3 text-primary-foreground" />}
                    </div>
                    <span className="text-sm">Eltérő számlázási cím használata</span>
                  </label>

                  {formData.useDifferentBilling && (
                    <div className="grid sm:grid-cols-2 gap-4 mt-6 pt-6 border-t border-border">
                      <div className="sm:col-span-2">
                        <label className="block text-sm font-medium mb-2">Számlázási cím *</label>
                        <input
                          type="text"
                          name="billingAddress"
                          value={formData.billingAddress}
                          onChange={handleChange}
                          className={`input-zinga ${errors.billingAddress ? 'border-destructive' : ''}`}
                        />
                        {errors.billingAddress && (
                          <p className="text-sm text-destructive mt-1">{errors.billingAddress}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Számlázási város *</label>
                        <input
                          type="text"
                          name="billingCity"
                          value={formData.billingCity}
                          onChange={handleChange}
                          className={`input-zinga ${errors.billingCity ? 'border-destructive' : ''}`}
                        />
                        {errors.billingCity && (
                          <p className="text-sm text-destructive mt-1">{errors.billingCity}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Számlázási irányítószám *</label>
                        <input
                          type="text"
                          name="billingZip"
                          value={formData.billingZip}
                          onChange={handleChange}
                          className={`input-zinga ${errors.billingZip ? 'border-destructive' : ''}`}
                        />
                        {errors.billingZip && (
                          <p className="text-sm text-destructive mt-1">{errors.billingZip}</p>
                        )}
                      </div>
                      <div className="sm:col-span-2">
                        <label className="block text-sm font-medium mb-2">Számlázási ország *</label>
                        <select
                          name="billingCountry"
                          value={formData.billingCountry}
                          onChange={handleChange}
                          className={`input-zinga ${errors.billingCountry ? 'border-destructive' : ''}`}
                        >
                          <option value="">Válassz országot</option>
                          <option value="HU">Magyarország</option>
                          <option value="AT">Ausztria</option>
                          <option value="SK">Szlovákia</option>
                          <option value="RO">Románia</option>
                          <option value="HR">Horvátország</option>
                        </select>
                        {errors.billingCountry && (
                          <p className="text-sm text-destructive mt-1">{errors.billingCountry}</p>
                        )}
                      </div>
                    </div>
                  )}
                </section>

                <section className="card-zinga">
                  <h2 className="font-display text-xl font-semibold mb-6">Szállítási mód</h2>
                  <div className="space-y-3">
                    <label
                      className={`flex flex-col items-start gap-2 p-4 rounded-xl border-2 cursor-pointer transition-colors sm:flex-row sm:items-center sm:justify-between ${
                        formData.shippingMethod === 'gls_home'
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-foreground/30'
                      }`}
                    >
                      <div className="flex w-full min-w-0 items-start gap-3">
                        <input
                          type="radio"
                          name="shippingMethod"
                          value="gls_home"
                          checked={formData.shippingMethod === 'gls_home'}
                          onChange={handleChange}
                          className="sr-only"
                        />
                        <div
                          className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                            formData.shippingMethod === 'gls_home' ? 'border-primary' : 'border-border'
                          }`}
                        >
                          {formData.shippingMethod === 'gls_home' && (
                            <div className="w-2.5 h-2.5 rounded-full bg-primary" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium">GLS házhozszállítás</p>
                          <p className="text-sm text-muted-foreground">2-4 munkanap</p>
                        </div>
                      </div>
                      <span className="self-end text-right font-medium sm:self-auto">2 190 Ft</span>
                    </label>

                    <label
                      className={`flex flex-col items-start gap-2 p-4 rounded-xl border-2 cursor-pointer transition-colors sm:flex-row sm:items-center sm:justify-between ${
                        isGlsParcelLocker
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-foreground/30'
                      }`}
                    >
                      <div className="flex w-full min-w-0 items-start gap-3">
                        <input
                          type="radio"
                          name="shippingMethod"
                          value="gls_parcel_locker"
                          checked={isGlsParcelLocker}
                          onChange={handleChange}
                          className="sr-only"
                        />
                        <div
                          className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                            isGlsParcelLocker ? 'border-primary' : 'border-border'
                          }`}
                        >
                          {isGlsParcelLocker && (
                            <div className="w-2.5 h-2.5 rounded-full bg-primary" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium">GLS csomagautomata</p>
                          <p className="text-sm text-muted-foreground">1-2 munkanap</p>
                        </div>
                      </div>
                      <span className="self-end text-right font-medium sm:self-auto">1 590 Ft</span>
                    </label>

                    <label
                      className={`flex flex-col items-start gap-2 p-4 rounded-xl border-2 cursor-pointer transition-colors sm:flex-row sm:items-center sm:justify-between ${
                        isPacketaParcelLocker
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-foreground/30'
                      }`}
                    >
                      <div className="flex w-full min-w-0 items-start gap-3">
                        <input
                          type="radio"
                          name="shippingMethod"
                          value="packeta_parcel_locker"
                          checked={isPacketaParcelLocker}
                          onChange={handleChange}
                          className="sr-only"
                        />
                        <div
                          className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                            isPacketaParcelLocker ? 'border-primary' : 'border-border'
                          }`}
                        >
                          {isPacketaParcelLocker && (
                            <div className="w-2.5 h-2.5 rounded-full bg-primary" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium">Packeta csomagautomata</p>
                          <p className="text-sm text-muted-foreground">1-2 munkanap</p>
                        </div>
                      </div>
                      <span className="self-end text-right font-medium sm:self-auto">1 190 Ft</span>
                    </label>

                    <label
                      className={`flex flex-col items-start gap-2 p-4 rounded-xl border-2 cursor-pointer transition-colors sm:flex-row sm:items-center sm:justify-between ${
                        formData.shippingMethod === 'personal_pickup_omega'
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-foreground/30'
                      }`}
                    >
                      <div className="flex w-full min-w-0 items-start gap-3">
                        <input
                          type="radio"
                          name="shippingMethod"
                          value="personal_pickup_omega"
                          checked={formData.shippingMethod === 'personal_pickup_omega'}
                          onChange={handleChange}
                          className="sr-only"
                        />
                        <div
                          className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                            formData.shippingMethod === 'personal_pickup_omega' ? 'border-primary' : 'border-border'
                          }`}
                        >
                          {formData.shippingMethod === 'personal_pickup_omega' && (
                            <div className="w-2.5 h-2.5 rounded-full bg-primary" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium">Személyes átvétel</p>
                          <p className="text-sm text-muted-foreground">Omega Sportcentrum</p>
                        </div>
                      </div>
                      <span className="self-end text-right font-medium sm:self-auto">0 Ft</span>
                    </label>

                    {isGlsParcelLocker && (
                      <div className="rounded-xl border border-border bg-secondary/10 p-4 space-y-3">
                        <p className="text-sm text-muted-foreground">
                          Válaszd ki a GLS csomagautomatát a térképen.
                        </p>
                        <button
                          type="button"
                          onClick={openGlsParcelLockerPicker}
                          disabled={!isGlsWidgetReady}
                          className="btn-secondary text-sm disabled:opacity-60"
                        >
                          {selectedParcelLocker ? 'Automata módosítása' : 'Automata kiválasztása'}
                        </button>
                        {!isGlsWidgetReady && (
                          <p className="text-xs text-muted-foreground">
                            GLS automata kereső betöltése folyamatban...
                          </p>
                        )}
                        {selectedParcelLocker && (
                          <div className="rounded-lg border border-border/70 bg-card p-3 text-sm">
                            <p className="font-medium">{selectedParcelLocker.name}</p>
                            <p className="text-muted-foreground">
                              ID: {selectedParcelLocker.id}
                            </p>
                            {(selectedParcelLocker.zip ||
                              selectedParcelLocker.city ||
                              selectedParcelLocker.address) && (
                              <p className="text-muted-foreground">
                                {[selectedParcelLocker.zip, selectedParcelLocker.city, selectedParcelLocker.address]
                                  .filter(Boolean)
                                  .join(' ')}
                              </p>
                            )}
                          </div>
                        )}
                        {errors.parcelLocker && (
                          <p className="text-sm text-destructive">{errors.parcelLocker}</p>
                        )}
                        <gls-dpm-dialog
                          className="block h-0 w-0 overflow-hidden"
                          ref={glsDialogRef}
                          country="hu"
                          language="hu"
                          filter-type="parcel-locker"
                        />
                      </div>
                    )}

                    {isPacketaParcelLocker && (
                      <div className="rounded-xl border border-border bg-secondary/10 p-4 space-y-3">
                        <p className="text-sm text-muted-foreground">
                          Válaszd ki a Packeta csomagpontot a térképen.
                        </p>
                        <button
                          type="button"
                          onClick={openPacketaPickupPointPicker}
                          disabled={!isPacketaWidgetReady}
                          className="btn-secondary text-sm disabled:opacity-60"
                        >
                          {selectedPacketaPickupPoint ? 'Csomagpont módosítása' : 'Csomagpont kiválasztása'}
                        </button>
                        {!isPacketaWidgetReady && (
                          <p className="text-xs text-muted-foreground">
                            Packeta pontválasztó betöltése folyamatban...
                          </p>
                        )}
                        {!packetaApiKey && (
                          <p className="text-xs text-destructive">
                            A Packeta API kulcs hiányzik (`REACT_APP_PACKETA_WIDGET_API_KEY`).
                          </p>
                        )}
                        {selectedPacketaPickupPoint && (
                          <div className="rounded-lg border border-border/70 bg-card p-3 text-sm">
                            <p className="font-medium">{selectedPacketaPickupPoint.name}</p>
                            <p className="text-muted-foreground">
                              ID: {selectedPacketaPickupPoint.id}
                            </p>
                            {(selectedPacketaPickupPoint.zip ||
                              selectedPacketaPickupPoint.city ||
                              selectedPacketaPickupPoint.address) && (
                              <p className="text-muted-foreground">
                                {[
                                  selectedPacketaPickupPoint.zip,
                                  selectedPacketaPickupPoint.city,
                                  selectedPacketaPickupPoint.address,
                                ]
                                  .filter(Boolean)
                                  .join(' ')}
                              </p>
                            )}
                          </div>
                        )}
                        {errors.packetaPickupPoint && (
                          <p className="text-sm text-destructive">{errors.packetaPickupPoint}</p>
                        )}
                      </div>
                    )}
                  </div>
                </section>

                <section className="card-zinga">
                  <h2 className="font-display text-xl font-semibold mb-6">Fizetési mód</h2>
                  <div className="space-y-3">
                    <label
                      className={`flex items-center p-4 rounded-xl border-2 cursor-pointer transition-colors ${
                        formData.paymentMethod === 'cod'
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-foreground/30'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="radio"
                          name="paymentMethod"
                          value="cod"
                          checked={formData.paymentMethod === 'cod'}
                          onChange={handleChange}
                          className="sr-only"
                        />
                        <div
                          className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                            formData.paymentMethod === 'cod' ? 'border-primary' : 'border-border'
                          }`}
                        >
                          {formData.paymentMethod === 'cod' && (
                            <div className="w-2.5 h-2.5 rounded-full bg-primary" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium">Utánvét</p>
                          <p className="text-sm text-muted-foreground">Fizess átvételkor</p>
                        </div>
                      </div>
                    </label>

                    <label
                      className={`flex items-center p-4 rounded-xl border-2 cursor-pointer transition-colors ${
                        formData.paymentMethod === 'card'
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-foreground/30'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="radio"
                          name="paymentMethod"
                          value="card"
                          checked={formData.paymentMethod === 'card'}
                          onChange={handleChange}
                          className="sr-only"
                        />
                        <div
                          className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                            formData.paymentMethod === 'card' ? 'border-primary' : 'border-border'
                          }`}
                        >
                          {formData.paymentMethod === 'card' && (
                            <div className="w-2.5 h-2.5 rounded-full bg-primary" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium">Bankkártyás fizetés</p>
                          <p className="text-sm text-muted-foreground">
                            Biztonságos fizetés Stripe-on keresztül (hamarosan)
                          </p>
                        </div>
                      </div>
                    </label>
                  </div>
                </section>

                <section className="card-zinga">
                  <h2 className="font-display text-xl font-semibold mb-6">Megjegyzés a rendeléshez (opcionális)</h2>
                  <textarea
                    name="orderNotes"
                    value={formData.orderNotes}
                    onChange={handleChange}
                    rows={3}
                    className="input-zinga resize-none"
                    placeholder="Bármilyen speciális kérés a rendeléssel kapcsolatban..."
                  />
                </section>

                <section className="card-zinga">
                  <div className="space-y-4">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        name="acceptTerms"
                        checked={formData.acceptTerms}
                        onChange={handleChange}
                        className="sr-only"
                      />
                      <div
                        className={`w-5 h-5 mt-0.5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                          formData.acceptTerms
                            ? 'bg-primary border-primary'
                            : errors.acceptTerms
                              ? 'border-destructive'
                              : 'border-border'
                        }`}
                      >
                        {formData.acceptTerms && <Check className="w-3 h-3 text-primary-foreground" />}
                      </div>
                      <span className="text-sm">
                        Elfogadom az{' '}
                        <Link to="/terms" className="underline hover:text-foreground/80">
                          Általános Szerződési Feltételeket
                        </Link>{' '}
                        *
                      </span>
                    </label>

                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        name="acceptPrivacy"
                        checked={formData.acceptPrivacy}
                        onChange={handleChange}
                        className="sr-only"
                      />
                      <div
                        className={`w-5 h-5 mt-0.5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                          formData.acceptPrivacy
                            ? 'bg-primary border-primary'
                            : errors.acceptPrivacy
                              ? 'border-destructive'
                              : 'border-border'
                        }`}
                      >
                        {formData.acceptPrivacy && <Check className="w-3 h-3 text-primary-foreground" />}
                      </div>
                      <span className="text-sm">
                        Elolvastam és tudomásul vettem az{' '}
                        <Link to="/privacy" className="underline hover:text-foreground/80">
                          Adatvédelmi Szabályzatot
                        </Link>{' '}
                        *
                      </span>
                    </label>

                    <div className="rounded-2xl border border-border/70 bg-secondary/30 p-4">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          name="saveAbandonedCheckout"
                          checked={formData.saveAbandonedCheckout}
                          onChange={handleChange}
                          className="sr-only"
                        />
                        <div
                          className={`w-5 h-5 mt-0.5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                            formData.saveAbandonedCheckout
                              ? 'bg-primary border-primary'
                              : 'border-border'
                          }`}
                        >
                          {formData.saveAbandonedCheckout && (
                            <Check className="w-3 h-3 text-primary-foreground" />
                          )}
                        </div>
                        <span className="text-sm">
                          Hozzájárulok, hogy ha nem fejezem be a vásárlást, a megadott adatokat és
                          a kosár tartalmát legfeljebb 14 napig elmentsék. A hozzájárulás nem
                          kötelező, és a jelölés visszavonásával a mentett adatok törlődnek.{' '}
                          <Link to="/privacy" className="underline hover:text-foreground/80">
                            Részletek
                          </Link>
                        </span>
                      </label>
                      {formData.saveAbandonedCheckout ? (
                        <p className={`mt-2 pl-8 text-xs ${
                          draftSaveStatus === 'error' ? 'text-destructive' : 'text-muted-foreground'
                        }`} aria-live="polite">
                          {draftSaveStatus === 'waitingForEmail'
                            ? 'A mentés érvényes e-mail-cím megadása után indul el.'
                            : draftSaveStatus === 'saving'
                              ? 'A pénztár adatainak mentése…'
                              : draftSaveStatus === 'saved'
                                ? 'A pénztár adatai biztonságosan elmentve.'
                                : draftSaveStatus === 'error'
                                  ? 'A mentés most nem sikerült. A rendelés ettől még leadható.'
                                  : ''}
                        </p>
                      ) : null}
                    </div>
                  </div>
                </section>
              </div>

              <div className="lg:col-span-1">
                <div className="card-zinga sticky top-24">
                  <h2 className="font-display text-xl font-semibold mb-6">Rendelés összesítő</h2>

                  <div className="space-y-4 mb-6">
                    {items.map((item) => (
                      <div key={item.product.id} className="flex gap-3">
                        <div className="w-16 h-16 bg-secondary/50 rounded-lg overflow-hidden flex-shrink-0">
                          <img
                            src={item.product.image}
                            alt={item.product.name}
                            className="w-full h-full object-cover"
                            style={{ objectPosition: getProductImageObjectPosition(item.product) }}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{item.product.name}</p>
                          <p className="text-sm text-muted-foreground">Db: {item.quantity}</p>
                        </div>
                        <p className="text-sm font-medium">
                          {(item.product.price * item.quantity).toLocaleString('hu-HU')} Ft
                        </p>
                      </div>
                    ))}
                  </div>

                  <div className="space-y-3 pt-6 border-t border-border">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Részösszeg</span>
                      <span>{subtotal.toLocaleString('hu-HU')} Ft</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Szállítás</span>
                      <span>{`${shippingCost.toLocaleString('hu-HU')} Ft`}</span>
                    </div>
                    {selectedPickupPoint && (
                      <div className="flex items-start justify-between gap-3 text-xs">
                        <span className="text-muted-foreground">Átvételi pont</span>
                        <span className="min-w-0 break-words text-right">{selectedPickupPoint.name}</span>
                      </div>
                    )}
                    <div className="pt-4 border-t border-border flex justify-between">
                      <span className="font-display font-semibold">Összesen</span>
                      <span className="font-display font-semibold text-lg">{total.toLocaleString('hu-HU')} Ft</span>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-primary w-full mt-6 disabled:opacity-70"
                  >
                    {isSubmitting ? 'Rendelés feldolgozása...' : 'Rendelés leadása'}
                  </button>

                  <p className="text-xs text-center text-muted-foreground mt-4">
                    A rendelésedet emailben visszaigazoljuk
                  </p>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
}
